import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileUp, FileCheck2, Users, CheckCircle2, AlertCircle } from "lucide-react";

const EMPTY = {
  name: "", phone: "", email: "", gstin: "",
  billing_address: "", gst_address: "",
  contract_start: "", contract_end: "",
  contact_person: "", notes: "",
};

export default function CustomersPage() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [delOpen, setDelOpen] = useState(null);

  const canWrite = ["accounts", "super_admin", "admin"].includes(user?.role);
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try { const { data } = await api.get("/customers"); setCustomers(data.customers); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setEdit(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setEdit({ ...EMPTY, ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/customers/${editing.id}`, edit);
      else await api.post(`/customers`, edit);
      toast.success(editing ? "Customer updated" : "Customer created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadAgreement = async (customerId, file) => {
    if (!file) return;
    try {
      const form = new FormData(); form.append("file", file);
      await api.post(`/customers/${customerId}/agreement`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Agreement uploaded"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/customers/${delOpen.id}`); toast.success("Customer removed"); setDelOpen(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-[#FF5A00]">CUSTOMER MASTER</div>
            <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="customers-page">Customers & Contracts</h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-xl">
              Every trip must be against an active customer contract. Create or edit customers here with GST + billing addresses and the signed agreement copy.
            </p>
          </div>
          {canWrite && (
            <Button onClick={openCreate} data-testid="new-customer-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-12 px-5">
              <Plus className="w-5 h-5 mr-2" /> New Customer
            </Button>
          )}
        </div>

        <div className="mt-10 grid gap-3">
          {customers.length === 0 && (
            <div className="p-10 border border-dashed border-[#2A2A2A] text-center text-zinc-500">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
              No customers yet. Create your first customer to start dispatching trips.
            </div>
          )}
          {customers.map((c) => (
            <div key={c.id} data-testid={`customer-row-${c.id}`} className="p-5 bg-[#141414] border border-[#2A2A2A]">
              <div className="grid lg:grid-cols-[1fr_auto] gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="font-display font-black text-xl">{c.name}</div>
                    {c.contract_active
                      ? <span data-testid={`contract-active-${c.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 rounded-sm"><CheckCircle2 className="w-3 h-3" /> Contract Active</span>
                      : <span data-testid={`contract-inactive-${c.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border border-rose-500/40 bg-rose-500/10 text-rose-400 rounded-sm"><AlertCircle className="w-3 h-3" /> Contract Expired / Not Set</span>}
                    {c.agreement_path && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400"><FileCheck2 className="w-3 h-3" /> Agreement on file</span>}
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3 mt-3 text-sm">
                    <div><div className="overline text-zinc-500 text-[10px]">PHONE</div><div className="font-mono">{c.phone || "—"}</div></div>
                    <div><div className="overline text-zinc-500 text-[10px]">EMAIL</div><div>{c.email || "—"}</div></div>
                    <div><div className="overline text-zinc-500 text-[10px]">GSTIN</div><div className="font-mono text-sky-400">{c.gstin || "—"}</div></div>
                    <div><div className="overline text-zinc-500 text-[10px]">CONTRACT</div><div className="font-mono">{c.contract_start || "—"} → {c.contract_end || "—"}</div></div>
                    <div><div className="overline text-zinc-500 text-[10px]">BILLING ADDRESS</div><div className="text-xs">{c.billing_address || "—"}</div></div>
                    <div><div className="overline text-zinc-500 text-[10px]">GST ADDRESS</div><div className="text-xs">{c.gst_address || "—"}</div></div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 lg:flex-col lg:items-end">
                  {canWrite && (
                    <label className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-wider font-bold border border-[#2A2A2A] rounded-sm cursor-pointer hover:bg-sky-500/10 text-sky-400">
                      <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`cust-agreement-upload-${c.id}`}
                        onChange={(e) => uploadAgreement(c.id, e.target.files?.[0])} />
                      <FileUp className="w-3 h-3 mr-1" /> {c.agreement_path ? "Replace agreement" : "Upload agreement"}
                    </label>
                  )}
                  {c.agreement_path && (
                    <a href={`${backend}/api/customers/${c.id}/agreement`} target="_blank" rel="noopener noreferrer"
                      data-testid={`cust-agreement-view-${c.id}`}
                      className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-wider font-bold border border-[#2A2A2A] rounded-sm hover:bg-white/5 text-emerald-400">
                      <FileCheck2 className="w-3 h-3 mr-1" /> View agreement
                    </a>
                  )}
                  {canWrite && (
                    <Button onClick={() => openEdit(c)} data-testid={`cust-edit-${c.id}`} size="sm" variant="outline"
                      className="rounded-sm border-[#2A2A2A] bg-transparent hover:bg-[#FF5A00]/10 text-[#FF5A00]"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                  )}
                  {canDelete && (
                    <Button onClick={() => setDelOpen(c)} data-testid={`cust-delete-${c.id}`} size="sm" variant="outline"
                      className="rounded-sm border-[#2A2A2A] bg-transparent hover:bg-rose-500/10 text-rose-400"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                  )}
                </div>
              </div>
              <PartyBlock customer={c} type="consignor" canWrite={canWrite} reload={load} />
              <PartyBlock customer={c} type="consignee" canWrite={canWrite} reload={load} />
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit: ${editing.name}` : "New Customer"}</DialogTitle>
            <DialogDescription className="text-zinc-400">Fill in GST details + contract period. Shipments cannot be prepared without an active contract.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Customer name *" k="name" e={edit} s={setEdit} />
            <F label="Contact person" k="contact_person" e={edit} s={setEdit} />
            <F label="Phone" k="phone" e={edit} s={setEdit} />
            <F label="Email" k="email" e={edit} s={setEdit} type="email" />
            <F label="GSTIN" k="gstin" e={edit} s={setEdit} />
            <div className="grid grid-cols-2 gap-2">
              <F label="Contract start *" k="contract_start" e={edit} s={setEdit} type="date" />
              <F label="Contract end *" k="contract_end" e={edit} s={setEdit} type="date" />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline text-zinc-500">Billing Address</Label>
              <Textarea data-testid="cust-billing_address-input" value={edit.billing_address || ""} onChange={(e) => setEdit({ ...edit, billing_address: e.target.value })}
                className="bg-[#0A0A0A] border-[#2A2A2A] mt-1 rounded-sm" rows={2} />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline text-zinc-500">GST / Registered Address</Label>
              <Textarea data-testid="cust-gst_address-input" value={edit.gst_address || ""} onChange={(e) => setEdit({ ...edit, gst_address: e.target.value })}
                className="bg-[#0A0A0A] border-[#2A2A2A] mt-1 rounded-sm" rows={2} />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline text-zinc-500">Internal notes</Label>
              <Textarea value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                className="bg-[#0A0A0A] border-[#2A2A2A] mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm border-[#2A2A2A]">Cancel</Button>
            <Button onClick={save} data-testid="cust-save-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delOpen} onOpenChange={() => setDelOpen(null)}>
        <AlertDialogContent className="bg-[#141414] border-[#2A2A2A] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer {delOpen?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Soft-delete. All historical trips remain intact. Only allowed when no open trips exist.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#2A2A2A] bg-transparent">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="cust-delete-confirm" className="rounded-sm bg-rose-600 hover:bg-rose-500 text-white font-bold">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, k, e, s, type = "text" }) {
  return (
    <div>
      <Label className="overline text-zinc-500">{label}</Label>
      <Input type={type} data-testid={`cust-${k}-input`} value={e[k] || ""} onChange={(ev) => s({ ...e, [k]: ev.target.value })}
        className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
    </div>
  );
}

function PartyBlock({ customer, type, canWrite, reload }) {
  const list = customer[type === "consignor" ? "consignors" : "consignees"] || [];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", gstin: "", phone: "" });
  const label = type === "consignor" ? "Consignors" : "Consignees";

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.post(`/customers/${customer.id}/parties`, { type, ...form });
      toast.success(`${type} added`); setForm({ name: "", address: "", gstin: "", phone: "" }); setOpen(false); reload?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (pid) => {
    if (!window.confirm("Remove this party?")) return;
    try { await api.delete(`/customers/${customer.id}/parties/${pid}`); toast.success(`${type} removed`); reload?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#2A2A2A]" data-testid={`${type}-block-${customer.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline text-[#FF5A00] text-[10px]">{label} ({list.length})</div>
        {canWrite && (
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid={`add-${type}-${customer.id}`}
            className="h-7 rounded-sm border-[#2A2A2A] text-xs hover:bg-[#FF5A00]/10 text-[#FF5A00]">
            <Plus className="w-3 h-3 mr-1" /> {open ? "Close" : `Add ${type}`}
          </Button>
        )}
      </div>
      {open && (
        <div className="grid sm:grid-cols-2 gap-2 p-3 bg-[#0A0A0A] border border-dashed border-[#2A2A2A] rounded-sm mb-3">
          <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid={`${type}-name-input`} className="h-9 bg-[#141414] border-[#2A2A2A] rounded-sm" />
          <Input placeholder="GSTIN" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })}
            data-testid={`${type}-gstin-input`} className="h-9 bg-[#141414] border-[#2A2A2A] rounded-sm" />
          <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid={`${type}-phone-input`} className="h-9 bg-[#141414] border-[#2A2A2A] rounded-sm" />
          <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid={`${type}-address-input`} className="h-9 bg-[#141414] border-[#2A2A2A] rounded-sm" />
          <div className="sm:col-span-2">
            <Button onClick={save} data-testid={`save-${type}-${customer.id}`} size="sm" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">Save {type}</Button>
          </div>
        </div>
      )}
      {list.length === 0 && !open && (
        <div className="text-xs text-zinc-500 italic">No {type}s yet. {canWrite && `Click "Add ${type}" to create one.`}</div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {list.map((p) => (
          <div key={p.id} data-testid={`${type}-item-${p.id}`} className="p-2 border border-[#2A2A2A] rounded-sm text-xs bg-[#0A0A0A]">
            <div className="font-semibold text-white">{p.name}</div>
            {p.gstin && <div className="text-sky-400 font-mono text-[10px]">{p.gstin}</div>}
            {p.phone && <div className="text-zinc-500 font-mono text-[10px]">{p.phone}</div>}
            {p.address && <div className="text-zinc-400 text-[10px] mt-1">{p.address}</div>}
            {canWrite && (
              <button onClick={() => remove(p.id)} data-testid={`remove-${type}-${p.id}`}
                className="mt-2 text-rose-400 hover:underline text-[10px]">Remove</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
